import type { GlAccount } from '../../../hooks/useGLAccounts.ts';
import {DocumentRelation, TFICDocument} from '../types/Document.ts';
import {useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode} from 'react';
import {v4 as uuidv4} from 'uuid';
import {BuildingOffice2Icon} from "@heroicons/react/24/outline";
import {CURRENCY_OPTIONS, DEFAULT_CURRENCY} from "../../../types/constants.ts";
import type { LineItemNeedingAttention } from '../types/LineItem.ts';
import Form, {useForm} from "../../../components/form-library/index.ts";
import Input from "../../../components/general/Input.tsx";
import FormDropdown from "../../../components/general/FormDropdown.tsx";
import {BookingErrorResponse, parseBookingErrors} from '../utils/parseBookingErrors.ts';
import {BackendApi} from '../../../api/api.ts';
import {ConvertedAmount} from './ConvertedAmount.tsx';
import {useDocumentValidation} from '../../../hooks/useDocumentValidation.ts';
import {LineItemsTable} from './LineItemsTable.tsx';
import {
    buildMergedLineItem,
    buildVatRateOptionsFromLineItems,
    buildSalesDirectionResetValues,
    calculateLineItemTotals,
    calculateVatAmountsFromNet,
    calculateVatAmountsFromTotal,
    computeAdjustTotalsForLastLine,
    computeMergedLineItemValues,
    getLineItemValuesFromForm,
    getTotalsDifference,
    hasLineItemBookingCategory,
    lineItemFieldName,
} from '../utils/lineItemsHelper.ts';
import {EditorActions, type SaveBlocker, type SaveState} from './EditorActions.tsx';
import {BulkActionsPopup} from './BulkActionsPopup.tsx';
import {TableActions} from './TableActions.tsx';
import {
    getNetAmountLockStatus,
    setNetAmountLockStatus,
} from '../utils/editorHelper.ts';
import {
    buildEditableBillFromForm,
    buildFormValuesFromEditableBill,
    buildLineItemFormValues,
    relationFromEditableBill,
    type EditorFormValues,
} from '../utils/editableBillMapping.ts';
import { validationResultFromError, type DocumentSavePayload } from '../utils/documentSave.ts';
import {
    BookingProvider,
    isDefaultBookingProvider
} from '../../../types/enums.ts';
import {
    DEFAULT_VAT_RATE_OPTIONS,
    DefaultVatRateOption,
} from '../types/VatCode.ts';
import {InfoTooltip} from "../../../components/general/InfoTooltip.tsx";
import {fieldErrorClasses} from "../../../components/general/fieldStyles.ts";
import LabeledToggle from "../../../components/general/LabeledToggle.tsx";
import {ScannedTotalsBanner} from './ScannedTotalsBanner.tsx';
import {SectionTitle} from './SectionTitle.tsx';
import {DocumentTypeChip} from './DocumentTypeChip.tsx';
import {AttentionSentence, CheckingBanner, CompleteBanner, type AttentionPoint} from './AttentionSentence.tsx';
import {BillTodoBanners} from './BillTodoBanners.tsx';
import type {BillTodo} from '../types/billTodo.ts';
import type {BillTodoType} from '../../../generated/enums.ts';
import { EditorSkeleton } from './EditorSkeleton.tsx';
import {useSaveModePreference, type SaveMode} from '../hooks/useSaveModePreference.ts';
import type {SaveOptions} from '../hooks/useDocumentDrawerService.ts';
import {useIsStuckToBottom} from '../hooks/useIsStuckToBottom.ts';
import {useMediaQuery} from '../hooks/useMediaQuery.ts';
import {cn} from '../../../lib/utils.ts';
import {ApplyScannedTotalsPopup} from './ApplyScannedTotalsPopup.tsx';
import {findLinesWithOnlyScannedTotal} from '../utils/scannedTotals.ts';
import {
    LINE_ITEMS_SECTION_SCROLL_FIELD,
    mapIssueToFormFieldName,
    processValidationIssues,
    type BannerItem,
    type ValidationResult,
} from '../utils/validationIssues.ts';
import MoneyInput from '../../../components/general/MoneyInput/index.ts';
import {isMoney, Money, toMoneyFromCents} from '../../../lib/Money.ts';
import {COUNTRIES_NL_SORTED} from "../../../types/countries.ts";
import {TotalsCheck} from './TotalsCheck.tsx';
import type {CurrencyEnum, DocumentCategory, DocumentDirection} from '../../../generated/enums.ts';

const tooltipText = "Wanneer de btw voor een document is aangevinkt, wordt de btw op dat document gebruikt voor de berekening van de realtime btw-indicatie. Je kunt de btw voor een document uitschakelen als het bijvoorbeeld privé is, niet-aftrekbaar, of als de factuur niet correct is. De btw telt dan niet mee voor de btw-berekeningen en de aangifte.";
const COUNTRY_OPTIONS = COUNTRIES_NL_SORTED.map(c => ({
    value: c.iso,
    label: c.nameNl,
    searchAliases: [c.country, c.iso],
}));

const CATEGORY_OPTIONS = [
    {value: 'invoice', label: 'Factuur'},
    {value: 'pos_receipt', label: 'Bonnetje'},
] satisfies Array<{ value: Exclude<DocumentCategory, 'other'>; label: string }>;

const DIRECTION_OPTIONS = [
    {value: 'purchase_invoice', label: 'Inkoop'},
    {value: 'sales_invoice', label: 'Verkoop'},
] satisfies Array<{ value: DocumentDirection; label: string }>;

const LINE_COLUMN_LABELS: Record<string, string> = {
    name_or_description: 'Omschrijving',
    net_amount: 'Bedrag excl.',
    vat_amount: 'BTW-bedrag',
    vat_rate_percentage_id: 'BTW',
    category_id: 'Grootboekrekening',
};

const DENSE_INPUT_CLASS = 'h-8 px-2.5 text-[13px] aria-[invalid=true]:border-red-400';
const DENSE_DROPDOWN_CLASS = '[&_input]:h-8 [&_input]:px-2.5 [&_input]:text-[13px] [&_button]:h-8 [&_button]:text-[13px]';
const STICKY_TOTALS_MEDIA_QUERY = '(min-width: 1024px) and (min-height: 40rem)';
const LINE_ITEMS_HIGHLIGHT_MS = 2000;
const TOGGLE_CLASS = 'h-8 justify-start gap-2 border-0 bg-transparent px-1.5 shadow-none [&_span]:text-[12px]';
const FORM_ITEM_STYLE = '[&_.form-item>label]:mb-0 [&_.form-item>label]:text-[12px] [&_.form-item>label]:font-semibold [&_.form-item>label_*]:font-semibold';

function fieldErrorDetails(fieldErrors: { name: string; errors: string[] }[]): string[] {
    const counts = new Map<string, number>();
    fieldErrors.forEach(({ name, errors }) => {
        const [prefix, , field] = name.split('.');
        const isLine = prefix === 'line_items' && field !== undefined;
        const message = errors[0] ?? '';
        const detail = isLine ? `${LINE_COLUMN_LABELS[field] ?? 'Regel'}: ${message}` : message;
        counts.set(detail, (counts.get(detail) ?? 0) + (isLine ? 1 : 0));
    });
    return [...counts].map(([detail, lines]) => (lines > 1 ? `${detail} (${lines} regels)` : detail));
}

function isSaveShortcut(event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey'>): boolean {
    return event.key === 'Enter' && (event.metaKey || event.ctrlKey);
}

export type DocumentEditorProps = {
    document: TFICDocument;

    bookingProvider: BookingProvider;
    isDocumentLoading: boolean;
    documentId: string;
    businessId: string;
    onSave: (data: DocumentSavePayload, options?: SaveOptions) => Promise<unknown> | void;
    onConvertToEuro?: () => Promise<void> | void;

    glAccounts: GlAccount[];
    lineItemsNeedingAttention?: LineItemNeedingAttention[];
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    onSaveAndNext?: (data: DocumentSavePayload) => Promise<void>;
    saveAndNextOnly?: boolean;
    saveNudge?: number;
    billTodos?: BillTodo[];
    onDismissTodo?: (todoType: BillTodoType) => Promise<void>;
    onShowDuplicates?: () => void;
    documentBarStart?: ReactNode;
};

const NO_TODOS: BillTodo[] = [];


export function DocumentEditor({
                                   document,

                                   bookingProvider,
                                   isDocumentLoading,
                                   documentId,
                                   businessId,
                                   onSave,
                                   onConvertToEuro,
                                   glAccounts,
                                   lineItemsNeedingAttention = [],
                                   setHasUnsavedChanges,
                                   onSaveAndNext,
                                   saveAndNextOnly = false,
                                   saveNudge = 0,
                                   billTodos = NO_TODOS,
                                   onDismissTodo,
                                   onShowDuplicates,
                                   documentBarStart,
                               }: DocumentEditorProps) {

    const hasDefaultBooking = isDefaultBookingProvider(bookingProvider);
    const canBookDocument = hasDefaultBooking;

    const [form] = useForm<EditorFormValues>();
    const [, setFormUiEpoch] = useState(0);
    const [lineItemIds, setLineItemIds] = useState<string[]>([]);
    const [lineItemsError, setLineItemsError] = useState<string | null>(null);
    const [bannerItems, setBannerItems] = useState<BannerItem[]>([]);
    const [errorDetails, setErrorDetails] = useState<string[]>([]);
    const [isBulkActionsOpen, setIsBulkActionsOpen] = useState(false);
    const [isScannedTotalsOpen, setIsScannedTotalsOpen] = useState(false);
    const [selectedDocumentRelation, setSelectedDocumentRelation] = useState<DocumentRelation | null>(
        relationFromEditableBill(document.editable_bill?.relation ?? null),
    );

    const [isConverting, setIsConverting] = useState(false);
    const [saveState, setSaveState] = useState<SaveState>('idle');
    const [isDirty, setIsDirty] = useState(false);
    const isSaving = saveState === 'saving';
    const saveMode = useSaveModePreference(onSaveAndNext !== undefined, saveAndNextOnly);
    const pendingSaveModeRef = useRef<SaveMode>('save');
    const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);

    const [defaultVatRateOptions, setDefaultVatRateOptions] = useState<DefaultVatRateOption[]>(DEFAULT_VAT_RATE_OPTIONS);
    const [formDataLoaded, setFormDataLoaded] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyEnum>(DEFAULT_CURRENCY);
    const [isNetAmountLocked, setIsNetAmountLocked] = useState(() => getNetAmountLockStatus());
    const lastAttentionScrollKeyRef = useRef<string | null>(null);
    const lineItemsSectionRef = useRef<HTMLDivElement | null>(null);
    const [isLineItemsHighlighted, setIsLineItemsHighlighted] = useState(false);

    const isRebuildingRowsRef = useRef(false);
    const isInitializingFormRef = useRef(false);
    const validatedLineItemIdsRef = useRef<string[]>([]);
    const bumpFormUi = () => setFormUiEpoch((n) => n + 1);

    const documentCategory = form.getFieldValue('document_category') as string | undefined;
    const documentDirection = (form.getFieldValue('document_direction') as DocumentDirection | null)
        ?? 'other';
    const shouldShowBookingError = hasLineItemBookingCategory(form, lineItemIds);
    const isCreditNote = !!form.getFieldValue('is_credit_note');
    const lineItemTotals = calculateLineItemTotals(getLineItemValuesFromForm(lineItemIds, form));
    const invoiceTotal = form.getFieldValue("total_amount_payable") as Money | undefined;
    const invoiceVatTotal = form.getFieldValue("vat_amount_overall") as Money | null | undefined;

    const hasLineItems = lineItemIds.length > 0;
    const totalsDifference = hasLineItems
        ? getTotalsDifference(
            invoiceTotal,
            invoiceVatTotal,
            lineItemTotals.netAmountsSum + lineItemTotals.vatAmountsSum,
            lineItemTotals.vatAmountsSum,
        )
        : null;
    const scannedTotalLines = findLinesWithOnlyScannedTotal(
        document.editable_bill?.line_items ?? [],
        lineItemIds.map((lineItemId) => ({
            lineItemId,
            description: (form.getFieldValue(lineItemFieldName(lineItemId, 'name_or_description')) as string | undefined) ?? '',
            netAmount: (form.getFieldValue(lineItemFieldName(lineItemId, 'net_amount')) as Money | null | undefined) ?? null,
        })),
    );
    const isScannedTotalsBannerShown = scannedTotalLines.length > 0;

    const api = useMemo(() => new BackendApi(), []);
    const monetaryOrigin = document.monetary_origin ?? null;
    // Euro shows what the conversion produced. Any other currency shows what was billed, relabelled:
    // correcting a mis-detected currency means the invoice's figures were right and only the currency
    // was wrong, so the billed numbers are the ones worth keeping.
    const amountsForCurrency = (target: CurrencyEnum): Record<string, Money | null> | null => {
        const editable = document.editable_bill;
        if (!editable || !monetaryOrigin) {
            return null;
        }
        const isEuro = target === 'EUR';
        const asTarget = (amount: Money | null | undefined): Money | null =>
            amount ? {...amount, currency: target} : null;
        const updates: Record<string, Money | null> = {
            total_amount_payable: isEuro
                ? ((editable.total_amount_payable as Money | null) ?? null)
                : asTarget(monetaryOrigin.original_fx_total_amount_payable),
            vat_amount_overall: isEuro
                ? ((editable.vat_amount_overall as Money | null) ?? null)
                : asTarget(monetaryOrigin.original_fx_vat_amount_overall),
        };
        for (const item of editable.line_items ?? []) {
            if (!item.id) {
                continue;
            }
            updates[lineItemFieldName(item.id, 'net_amount')] = isEuro
                ? ((item.net_amount as Money | null) ?? null)
                : asTarget(item.original_fx_net_amount as Money | null);
            updates[lineItemFieldName(item.id, 'vat_amount')] = isEuro
                ? ((item.vat_amount as Money | null) ?? null)
                : asTarget(item.original_fx_vat_amount as Money | null);
        }
        return updates;
    };

    const billedLineAmounts = useMemo(() => {
        const byId: Record<string, {net?: Money | null; vat?: Money | null}> = {};
        for (const item of document.editable_bill?.line_items ?? []) {
            if (item.id && (item.original_fx_net_amount || item.original_fx_vat_amount)) {
                byId[item.id] = {
                    net: item.original_fx_net_amount as Money | null,
                    vat: item.original_fx_vat_amount as Money | null,
                };
            }
        }
        return byId;
    }, [document.editable_bill]);
    const {
        result: validationResult,
        isChecking,
        isAvailable,
        seedResult,
        requestValidation,
    } = useDocumentValidation<DocumentSavePayload, ValidationResult>({
        documentId,
        validate: (payload, signal) =>
            api.withAbortSignal(signal).Partner__client__document__validateDocumentFields(payload),
    });

    useEffect(() => {
        if (!validationResult) {
            return;
        }
        const ids = validatedLineItemIdsRef.current;
        const processed = processValidationIssues(
            validationResult.issues,
            ids,
            new Set(form.getRegisteredFieldNames()),
        );
        form.setExternalErrors(processed.fieldErrors);
        setLineItemsError(processed.lineItemsError);
        setBannerItems(processed.bannerItems);
        setErrorDetails(fieldErrorDetails(processed.fieldErrors));
    }, [validationResult, form]);

    useEffect(() => {
        if (isAvailable) {
            return;
        }
        form.clearFieldsChecking();
        console.warn('Live validatie is tijdelijk niet beschikbaar');
    }, [isAvailable, form]);

    const buildPayload = (values: EditorFormValues, ids: string[]): DocumentSavePayload => ({
        document_id: documentId,
        business_id: businessId,
        editable_bill: buildEditableBillFromForm(
            values,
            ids,
            form,
            defaultVatRateOptions,
            selectedDocumentRelation ?? null,
        ),
    });

    const requestValidationFor = (ids: string[]) => {
        validatedLineItemIdsRef.current = ids;
        requestValidation(buildPayload(form.getFieldsValue(true) as EditorFormValues, ids));
    };

    const reportUnsavedChanges = (dirty: boolean) => {
        setIsDirty(dirty);
        setHasUnsavedChanges(dirty);
        if (dirty) {
            setSaveState((state) => (state === 'saved' ? 'idle' : state));
        }
    };

    const handleSave = async (payload: DocumentSavePayload, mode: SaveMode, options?: SaveOptions) => {
        setSaveState('saving');
        let saved = false;
        try {
            if (mode === 'save_and_next' && onSaveAndNext) {
                await onSaveAndNext(payload);
            } else {
                await onSave(payload, options);
            }
            reportUnsavedChanges(false);
            saved = true;
            setSaveState('saved');
            if (savedStatusTimerRef.current !== null) {
                clearTimeout(savedStatusTimerRef.current);
            }
            savedStatusTimerRef.current = setTimeout(() => {
                savedStatusTimerRef.current = null;
                setSaveState((state) => (state === 'saved' ? 'idle' : state));
            }, 5000);
        } catch (bookingError: unknown) {
            const error = bookingError as BookingErrorResponse;
            const validationResult = validationResultFromError(error);
            if (validationResult) {
                validatedLineItemIdsRef.current = lineItemIds;
                seedResult(validationResult);
                const firstIssue = validationResult.issues[0];
                const firstField = firstIssue ? mapIssueToFormFieldName(firstIssue, lineItemIds) : null;
                if (firstField) {
                    scrollToField(firstField);
                }
                return;
            }

            if (error?.detail) {
                const parsedErrors = parseBookingErrors(error);
                if (parsedErrors.errors.length > 0) {
                    form.setFields(parsedErrors.errors);

                    if (parsedErrors.hasLineItemsError) {
                        const lineItemsErrorMsg = parsedErrors.errors.find(err => err.name === 'line_items')?.errors[0] || parsedErrors.generalMessage || 'Er is een fout met de regels';
                        setLineItemsError(lineItemsErrorMsg);
                    }
                }
            }
        } finally {
            if (!saved) {
                setSaveState('idle');
            }
        }
    };

    const handleFormSubmit = async (values: EditorFormValues) => {
        await handleSave(buildPayload(values, lineItemIds), pendingSaveModeRef.current);
    };

    const submit = (mode: SaveMode) => {
        pendingSaveModeRef.current = mode;
        form.submit();
    };


    const initializeForm = async () => {
        if (isDocumentLoading) return;

        setFormDataLoaded(false);
        isInitializingFormRef.current = true;
        reportUnsavedChanges(false);

        try {
            const editableBill = document.editable_bill;
            if (document.document_category === "other" || !editableBill) {
                setLineItemIds([]);
                form.resetFields();
                return;
            }

            const vatRateOptions = buildVatRateOptionsFromLineItems(editableBill.line_items);
            const headerValues = buildFormValuesFromEditableBill(editableBill, document.client_business_name ?? null);
            const lineItems = buildLineItemFormValues(editableBill.line_items, vatRateOptions);

            form.setFieldsValue({ ...headerValues, ...lineItems.values });
            setSelectedCurrency(headerValues.currency);
            setSelectedDocumentRelation(relationFromEditableBill(editableBill.relation));
            const ids = editableBill.line_items.map(item => item.id ?? '').filter(id => id !== '');
            validatedLineItemIdsRef.current = ids;
            setLineItemIds(ids);
            setDefaultVatRateOptions(vatRateOptions);
            setFormDataLoaded(true);
        } finally {
            isInitializingFormRef.current = false;
        }
    }


    const handleFormValuesChange = (changedValues: Partial<EditorFormValues>) => {
        if (changedValues.document_direction === 'sales_invoice' && !isInitializingFormRef.current) {
            form.setFieldsValue(buildSalesDirectionResetValues(lineItemIds));
        }

        if ('currency' in changedValues) {
            const newCurrency = changedValues.currency;
            if (newCurrency) {
                setSelectedCurrency(newCurrency);
                const known = amountsForCurrency(newCurrency);
                if (known) {
                    form.setFieldsValue(known);
                } else {
                    const formValues = form.getFieldsValue(true);
                    const moneyUpdates: Record<string, Money> = {};

                    Object.entries(formValues).forEach(([key, value]) => {
                        if (isMoney(value)) {
                            moneyUpdates[key] = {
                                ...value,
                                currency: newCurrency,
                            };
                        }
                    });
                    const nestedRows = form.getFieldValue('line_items');
                    if (nestedRows && typeof nestedRows === 'object' && !Array.isArray(nestedRows)) {
                        Object.entries(nestedRows).forEach(([id, row]) => {
                            if (!row || typeof row !== 'object') {
                                return;
                            }
                            Object.entries(row).forEach(([field, value]) => {
                                if (isMoney(value)) {
                                    moneyUpdates[lineItemFieldName(id, field)] = {
                                        ...value,
                                        currency: newCurrency,
                                    };
                                }
                            });
                        });
                    }
                    form.setFieldsValue(moneyUpdates);
                }
            }
        }

        bumpFormUi();

        if (formDataLoaded && !isRebuildingRowsRef.current && !isInitializingFormRef.current) {
            reportUnsavedChanges(true);
            const changedFieldNames = Object.keys(changedValues);
            if (changedFieldNames.length > 0) {
                form.setFieldsChecking(changedFieldNames);
            }
            requestValidationFor(lineItemIds);
        }
    };

    const rebuildRows = (nextLineItemIds: string[], mutate: () => void) => {
        isRebuildingRowsRef.current = true;
        try {
            setLineItemIds(nextLineItemIds);
            mutate();
        } finally {
            isRebuildingRowsRef.current = false;
        }

        if (formDataLoaded) {
            reportUnsavedChanges(true);
            requestValidationFor(nextLineItemIds);
        }
    };

    const setLineItemFieldAmount = (fieldName: string, cents: number, currency: CurrencyEnum) => {
        form.setFieldValue(fieldName, toMoneyFromCents(cents, currency));
        bumpFormUi();
    };


    const handleLineItemVatRateChange = (itemId: string, vatRatePercentage: number | null) => {
        const netField = lineItemFieldName(itemId, 'net_amount');
        const vatField = lineItemFieldName(itemId, 'vat_amount');
        const currency = form.getFieldValue('currency') || DEFAULT_CURRENCY;
        const netCents = (form.getFieldValue(netField) as Money | null)?.signed_amount ?? 0;
        const vatCents = (form.getFieldValue(vatField) as Money | null)?.signed_amount ?? 0;
        const lineTotal = netCents + vatCents;
        const rate = vatRatePercentage ?? 0;

        if (isNetAmountLocked) {
            const {vatAmount} = calculateVatAmountsFromNet(netCents, rate);
            setLineItemFieldAmount(vatField, vatAmount, currency);
        }
        else{
            const {netAmount, vatAmount} = calculateVatAmountsFromTotal(lineTotal, rate);
            setLineItemFieldAmount(netField, netAmount, currency);
            setLineItemFieldAmount(vatField, vatAmount, currency);
        }
    }

    const handleLineItemNetAmountChange = (itemId: string, netAmount: Money | null) => {
        const vatAmountFieldName = lineItemFieldName(itemId, "vat_amount");
        const vatRatePercentageFieldName = lineItemFieldName(itemId, "vat_rate_percentage_id");

        const vatRatePercentageId = form.getFieldValue(vatRatePercentageFieldName);

        let vatRate = defaultVatRateOptions.find(opt => opt.value === vatRatePercentageId)?.percentage;
        const currentLineItemNetAmountCents = netAmount?.signed_amount ?? 0;

        const {vatAmount: currentLineItemVatAmountCents} = calculateVatAmountsFromNet(currentLineItemNetAmountCents, vatRate ?? 0);

        const currency = form.getFieldValue('currency') || 'EUR';

        form.setFieldValue(vatAmountFieldName, toMoneyFromCents(currentLineItemVatAmountCents, currency));
        bumpFormUi();
    }

    const handleAddRow = () => {
        const newItemId = uuidv4();

        rebuildRows([...lineItemIds, newItemId], () => {
            form.setFieldValue(`line_items.${newItemId}`, {
                name_or_description: '',
                net_amount: null,
                vat_id: '',
                vat_amount: null,
                transitory_schedule: null,
                subledger_id: null,
            });
        });
        return newItemId;
    }

    const handleMergeLineItems = () => {
        if (lineItemIds.length < 2) return;

        const firstItemId = lineItemIds[0];
        const firstLineNameOrDescription = form.getFieldValue(lineItemFieldName(firstItemId, "name_or_description"));

        const relationName = form.getFieldValue('relation_name');
        const firstCategoryId = form.getFieldValue(lineItemFieldName(firstItemId, "category_id"));
        const formCurrency = form.getFieldValue('currency');

        const lineItemFields = {
            nameOrDescription: relationName || firstLineNameOrDescription || '',
            firstCategoryId: firstCategoryId || null,
        };

        const billTotalAmountMoney = form.getFieldValue("total_amount_payable") as Money | undefined;
        const billVatAmountMoney = form.getFieldValue("vat_amount_overall") as Money | null | undefined;
        const billTotalAmountsCents = billTotalAmountMoney?.signed_amount ?? 0;
        const billVatAmountsCents = billVatAmountMoney?.signed_amount ?? 0;

        const mergedLineItemAmounts = computeMergedLineItemValues(
            billTotalAmountsCents,
            billVatAmountsCents,
            defaultVatRateOptions,
        );
        const newItemId = uuidv4();

        rebuildRows([newItemId], () => {
            lineItemIds.forEach(id => form.setFieldValue(`line_items.${id}`, undefined));
            form.setFieldValue(`line_items.${newItemId}`, buildMergedLineItem({
                ...lineItemFields,
                ...mergedLineItemAmounts,
                currency: formCurrency,
            }));
        });
    }

    const handleDeleteRow = (itemId: string) => {
        const nextIds = lineItemIds.filter(id => id !== itemId);
        rebuildRows(nextIds, () => {
            form.setFieldValue(`line_items.${itemId}`, undefined);
        });
    }

    const handleBulkApply = (
        vatId: string | null,
        defaultBookCategoryId: string | null,
        flipSign: boolean = false,
        fromTotalToNet: boolean = false,
        mergeAllLineItems: boolean = false
    ) => {
        if (lineItemIds.length === 0) return;
        if (mergeAllLineItems) {
            handleMergeLineItems();
            return;
        }

        const bulkFormValues: Record<string, any> = {};
        const currency = form.getFieldValue('currency') || 'EUR';

        lineItemIds.forEach((itemId) => {
            const netAmountFieldName = lineItemFieldName(itemId, "net_amount");
            const vatAmountFieldName = lineItemFieldName(itemId, "vat_amount");
            const vatIdFieldName = lineItemFieldName(itemId, "vat_id");
            const vatRatePercentageIdFieldName = lineItemFieldName(itemId, "vat_rate_percentage_id");

            const currentNetAmountMoney = form.getFieldValue(netAmountFieldName) as Money | null;
            const currentVatAmountMoney = form.getFieldValue(vatAmountFieldName) as Money | null;

            let netCents = currentNetAmountMoney?.signed_amount ?? 0;
            let vatCents = currentVatAmountMoney?.signed_amount ?? 0;
            let changed = false;

            if (fromTotalToNet) {
                let targetVatRate: number | undefined;
                if (vatId !== null) {
                        bulkFormValues[vatRatePercentageIdFieldName] = vatId;
                        targetVatRate = defaultVatRateOptions.find(opt => opt.value === vatId)?.percentage;
                } else {
                    const activeVatId = form.getFieldValue(vatIdFieldName);
                    const activeVatRateId = form.getFieldValue(vatRatePercentageIdFieldName);
                    if (activeVatRateId) {
                        targetVatRate = defaultVatRateOptions.find(opt => opt.value === activeVatRateId)?.percentage;
                    }
                }

                if (targetVatRate !== undefined) {
                    const {vatAmount, netAmount} = calculateVatAmountsFromTotal(netCents, targetVatRate);
                    netCents = netAmount;
                    vatCents = vatAmount;
                    changed = true;
                }
            } else if (vatId !== null) {
                let vatRatePercentage = 0;
                    bulkFormValues[vatRatePercentageIdFieldName] = vatId;
                    vatRatePercentage = defaultVatRateOptions.find(opt => opt.value === vatId)?.percentage ?? 0;

                if (isNetAmountLocked) {
                    const {vatAmount} = calculateVatAmountsFromNet(netCents, vatRatePercentage);
                    vatCents = vatAmount;
                } else {
                    const totalCents = netCents + vatCents;
                    const {vatAmount, netAmount} = calculateVatAmountsFromTotal(totalCents, vatRatePercentage);
                    netCents = netAmount;
                    vatCents = vatAmount;
                }
                changed = true;
            }

            if (flipSign) {
                netCents *= -1;
                vatCents *= -1;
                changed = true;
            }

            if (changed) {
                bulkFormValues[netAmountFieldName] = toMoneyFromCents(netCents, currency);
                bulkFormValues[vatAmountFieldName] = toMoneyFromCents(vatCents, currency);
            }


            if (defaultBookCategoryId !== null && hasDefaultBooking) {
                bulkFormValues[lineItemFieldName(itemId, "category_id")] = defaultBookCategoryId;
            }
        });

        form.setFieldsValue(bulkFormValues);
        bumpFormUi();

        form.validateFields(Object.keys(bulkFormValues))
    };

    const handleApplyScannedTotals = () => {
        const currency = form.getFieldValue('currency') || DEFAULT_CURRENCY;
        const netAmountFields = scannedTotalLines.map((line) => {
            const netAmountField = lineItemFieldName(line.lineItemId, 'net_amount');
            const netAmount = toMoneyFromCents(line.totalAmount.signed_amount, currency);
            form.setFieldValue(netAmountField, netAmount);
            handleLineItemNetAmountChange(line.lineItemId, netAmount);
            return netAmountField;
        });
        form.validateFields(netAmountFields);
    };

    const handleAdjustTotals = () => {
        if (!totalsDifference || !lineItemIds.length) return;
        const lastId = lineItemIds[lineItemIds.length - 1];
        const currency = form.getFieldValue('currency') || DEFAULT_CURRENCY;
        const lineItemValues = getLineItemValuesFromForm(lineItemIds, form);
        if (lineItemValues.length === 0) return;
        const lastLineItem = lineItemValues[lineItemValues.length - 1];
        if (!lastLineItem) return;

        const calculated = calculateLineItemTotals(lineItemValues);
        const { adjustedNetCents, adjustedVatCents } = computeAdjustTotalsForLastLine(
            totalsDifference.grossDiff,
            totalsDifference.vatDiff,
            lastLineItem.netAmount,
            lastLineItem.vatAmount,
            calculated.netAmountsSum,
            calculated.vatAmountsSum
        );

        form.setFieldsValue({
            [lineItemFieldName(lastId, 'net_amount')]: toMoneyFromCents(adjustedNetCents, currency),
            [lineItemFieldName(lastId, 'vat_amount')]: toMoneyFromCents(adjustedVatCents, currency),
        });
        bumpFormUi();
    };

    useEffect(() => {
        const loadDocumentData = async () => {
            await initializeForm();
        }

        loadDocumentData();
    }, [document, businessId]);


    const saveBlocker: SaveBlocker =
        document.is_booked === true ? 'booked'
            : isChecking ? 'checking'
                : validationResult != null && !validationResult.is_complete ? 'invalid'
                    : null;

    const scrollToField = (fieldName: string) => {
        requestAnimationFrame(() => {
            if (fieldName === LINE_ITEMS_SECTION_SCROLL_FIELD) {
                lineItemsSectionRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                return;
            }
            form.scrollToField(fieldName, {
                behavior: "smooth",
                block: "center",
            });
        });
    };

    const scrollToFirstAttentionLineItem = () => {
        if (lineItemsNeedingAttention.length === 0) {
            lastAttentionScrollKeyRef.current = null;
            return;
        }
        if (!formDataLoaded || !hasBeenReady) {
            return;
        }

        const firstLineItemId = lineItemsNeedingAttention[0]?.lineItemId;
        if (!firstLineItemId) {
            lastAttentionScrollKeyRef.current = null;
            return;
        }

        const scrollKey = `${documentId}:${firstLineItemId}`;
        if (lastAttentionScrollKeyRef.current === scrollKey) {
            return;
        }

        lastAttentionScrollKeyRef.current = scrollKey;
        scrollToField(lineItemFieldName(firstLineItemId, 'name_or_description'));
    };

    const scrollToFirstFieldError = () => {
        const issues = validationResult?.issues ?? [];
        const processed = processValidationIssues(
            issues,
            lineItemIds,
            new Set(form.getRegisteredFieldNames()),
        );
        const firstField = processed.fieldErrors[0]?.name;
        if (firstField) {
            scrollToField(firstField);
        }
    };

    const handleBannerScroll = (fieldName: string | null) => {
        if (fieldName) {
            scrollToField(fieldName);
            return;
        }
        scrollToFirstFieldError();
    };

    useEffect(() => {
        if (!formDataLoaded) {
            setLineItemsError(null);
            setBannerItems([]);
            setErrorDetails([]);
            return;
        }

        if (document.validation_result) {
            seedResult(document.validation_result);
        }
    }, [formDataLoaded, document.validation_result, seedResult]);

    useEffect(() => {
        if (!isLineItemsHighlighted) {
            return;
        }
        const timer = setTimeout(() => setIsLineItemsHighlighted(false), LINE_ITEMS_HIGHLIGHT_MS);
        return () => clearTimeout(timer);
    }, [isLineItemsHighlighted]);

    const highlightLineItems = () => {
        scrollToField(LINE_ITEMS_SECTION_SCROLL_FIELD);
        setIsLineItemsHighlighted(true);
    };

    const focusInvoiceDate = () => {
        const dateInput = rootRef.current?.querySelector<HTMLInputElement>('input[data-field="normalized_date"]');
        if (!dateInput) {
            return;
        }
        dateInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        dateInput.focus({ preventScroll: true });
    };

    const goToFirstIssue = () => {
        const firstOnScreen = rootRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
        if (firstOnScreen) {
            firstOnScreen.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstOnScreen.focus({ preventScroll: true });
            return;
        }
        const firstBannerField = bannerItems.flatMap((item) => (item.kind === 'message' && item.fieldName ? [item.fieldName] : []))[0];
        if (firstBannerField) {
            scrollToField(firstBannerField);
            return;
        }
        if (lineItemsError) {
            scrollToField(LINE_ITEMS_SECTION_SCROLL_FIELD);
            return;
        }
        handleBannerScroll(null);
    };
    const goToFirstIssueRef = useRef(goToFirstIssue);
    useEffect(() => {
        goToFirstIssueRef.current = goToFirstIssue;
    });
    const onReviewIssues = useCallback(() => goToFirstIssueRef.current(), []);

    const attentionPoints = useMemo<AttentionPoint[]>(
        () => bannerItems.map((item) =>
            item.kind === 'field_errors'
                ? {
                    label: item.count === 1 ? '1 veld heeft een fout' : `${item.count} velden hebben een fout`,
                    details: errorDetails,
                }
                : { label: item.message, details: [] },
        ),
        [bannerItems, errorDetails],
    );
    const hasValidationResult = validationResult != null;

    const [shownAttentionPoints, setShownAttentionPoints] = useState<AttentionPoint[] | null>(null);
    const [hadAttentionPoints, setHadAttentionPoints] = useState(false);
    const {isStuck: isTotalsStuck, sentinelRef: totalsSentinelRef} = useIsStuckToBottom();
    const canStickTotals = useMediaQuery(STICKY_TOTALS_MEDIA_QUERY);

    useEffect(() => {
        if (!formDataLoaded) {
            setShownAttentionPoints(null);
            return;
        }
        if (hasValidationResult && !isChecking) {
            setShownAttentionPoints(attentionPoints);
            if (attentionPoints.length > 0) {
                setHadAttentionPoints(true);
            }
        }
    }, [formDataLoaded, hasValidationResult, isChecking, attentionPoints]);

    const hasEditableBill = document.document_category !== 'other' && document.editable_bill != null;
    const isFirstValidationShown = shownAttentionPoints !== null || document.validation_result == null;
    const isEditorReady = !hasEditableBill || (formDataLoaded && isFirstValidationShown);
    const [hasBeenReady, setHasBeenReady] = useState(false);

    useEffect(() => {
        if (isEditorReady) {
            setHasBeenReady(true);
        }
    }, [isEditorReady]);

    useEffect(() => {
        scrollToFirstAttentionLineItem();
    }, [formDataLoaded, hasBeenReady, lineItemsNeedingAttention, documentId, form]);

    useEffect(() => () => {
        setHasUnsavedChanges(false);
    }, [setHasUnsavedChanges]);

    useEffect(() => () => {
        if (savedStatusTimerRef.current !== null) {
            clearTimeout(savedStatusTimerRef.current);
        }
    }, []);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!(event.target instanceof Node) || !rootRef.current?.contains(event.target)) return;
        if (isSaveShortcut(event) && saveBlocker === null && !isSaving) {
            event.preventDefault();
            submit(saveMode.mode);
        }
    };

    const currency = form.getFieldValue('currency') || DEFAULT_CURRENCY;
    const clientName = form.getFieldValue('client_name') as string | undefined;
    const isBooked = document.is_booked === true;

    return (
        <>
            {!hasBeenReady && <EditorSkeleton />}
            <div ref={rootRef} onKeyDown={handleKeyDown} className={cn('flex min-h-0 flex-1 flex-col', !hasBeenReady && 'hidden')}>
                <div className={cn('scrollbar-subtle flex min-h-0 flex-1 scroll-pb-40 flex-col overflow-y-auto [scrollbar-gutter:stable] px-4 pb-3 pt-3', isBooked && 'opacity-70')}>
                    {isBooked && (
                        <div role="status" className="mb-5 rounded-md border border-primary/30 bg-superliteprimary px-3 py-2 text-[13px] text-darkprimary">
                            Dit document is al geboekt en kan niet langer aangepast worden. Het kan wel aangepast worden in het boekhoudpakket.
                        </div>
                    )}

                <Form
                    form={form}
                    scrollToFirstError
                    onValuesChange={handleFormValuesChange}
                    onFinish={handleFormSubmit}
                    className={cn('space-y-5', FORM_ITEM_STYLE, isBooked && 'pointer-events-none')}
                >
                    {shownAttentionPoints === null
                        ? <CheckingBanner />
                        : shownAttentionPoints.length > 0
                            ? <AttentionSentence points={shownAttentionPoints} onReview={onReviewIssues} />
                            : hadAttentionPoints && <CompleteBanner />}

                    {onDismissTodo && onShowDuplicates && (
                        <BillTodoBanners
                            todos={billTodos}
                            onDismiss={onDismissTodo}
                            onSetTransitorySchedule={isBooked ? undefined : highlightLineItems}
                            onChangeInvoiceDate={isBooked ? undefined : focusInvoiceDate}
                            onShowDuplicates={onShowDuplicates}
                        />
                    )}


                    <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 px-3 py-2 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="mr-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500">Document</span>
                                    <Form.Item name="document_category" required>
                                        <DocumentTypeChip label="Categorie" options={CATEGORY_OPTIONS} />
                                    </Form.Item>
                                    <Form.Item name="document_direction" required>
                                        <DocumentTypeChip label="Inkoop/verkoop" options={DIRECTION_OPTIONS} />
                                    </Form.Item>
                                </div>
                                <span className="hidden h-5 w-px bg-gray-200 md:block" aria-hidden="true" />
                                <div className="flex flex-wrap items-center gap-x-1">
                                    <Form.Item name="is_vat_toggled_on" valuePropName="checked">
                                        <LabeledToggle
                                            label="BTW meenemen"
                                            accessory={<InfoTooltip position="top" text={tooltipText}/>}
                                            className={TOGGLE_CLASS}
                                        />
                                    </Form.Item>
                                    {documentCategory === 'invoice' && (
                                        <Form.Item name="vat_reverse_charge_applied" valuePropName="checked">
                                            <LabeledToggle label="BTW verlegd" className={TOGGLE_CLASS}/>
                                        </Form.Item>
                                    )}
                                    {(documentCategory === 'invoice'
                                        || documentCategory === 'pos_receipt') && (
                                        <Form.Item name="is_credit_note" valuePropName="checked">
                                            <LabeledToggle label="Creditnota" className={TOGGLE_CLASS}/>
                                        </Form.Item>
                                    )}
                                </div>
                            </div>
                            <div className="flex max-w-full flex-wrap items-center gap-2">
                                {documentBarStart}
                                {clientName && (
                                    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary/25 bg-superliteprimary px-2.5 py-1 text-[12px] font-semibold text-darkprimary" title="PocketCFO-klant">
                                        <BuildingOffice2Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                        <span className="truncate">{clientName}</span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <section aria-labelledby="document-facts-title" className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                        <SectionTitle><span id="document-facts-title">Relatie</span></SectionTitle>
                        <div className="mt-2 grid grid-cols-12 gap-x-3 gap-y-3">
                            <Form.Item name="relation_name" label="Relatie" required className="col-span-12 md:col-span-6">
                                <Input type="text" selectOnFocus placeholder="Naam van de relatie" className={DENSE_INPUT_CLASS}/>
                            </Form.Item>
                            <Form.Item
                                name="relation_country"
                                label="Land van relatie"
                                required
                                className={cn('col-span-12 md:col-span-6', DENSE_DROPDOWN_CLASS)}
                            >
                                <FormDropdown
                                    options={COUNTRY_OPTIONS}
                                    searchable
                                    anchor={false}
                                    openMenuOnFocus
                                    classNames={{ input: 'w-full min-w-0', options: 'py-2' }}
                                />
                            </Form.Item>
                            <Form.Item name="relation_iban" label="IBAN" className="col-span-12 md:col-span-6">
                                <Input type="text" selectOnFocus placeholder="NL00 BANK 0000 0000 00" className={DENSE_INPUT_CLASS}/>
                            </Form.Item>
                            <Form.Item name="vat_number" label="BTW-nummer" className="col-span-12 md:col-span-6">
                                <Input type="text" selectOnFocus placeholder="NL000000000B01" className={DENSE_INPUT_CLASS}/>
                            </Form.Item>
                        </div>

                        <div className="mt-4 border-t border-gray-100 pt-3">
                            <SectionTitle>Factuurgegevens</SectionTitle>
                            <div className="mt-2 grid grid-cols-12 gap-x-3 gap-y-3">
                                <Form.Item
                                    name="reference_identifier"
                                    label="Factuurnummer"
                                    required={documentCategory !== 'pos_receipt'}
                                    className="col-span-6 md:col-span-3"
                                >
                                    <Input type="text" selectOnFocus className={DENSE_INPUT_CLASS}/>
                                </Form.Item>
                                <Form.Item name="normalized_date" label="Factuurdatum" required className="col-span-6 md:col-span-3">
                                    <Input type="date" data-field="normalized_date" className={DENSE_INPUT_CLASS}/>
                                </Form.Item>
                                {documentCategory === 'invoice' && !isCreditNote && (
                                    <Form.Item name="normalized_due_date" label="Vervaldatum" className="col-span-6 md:col-span-3">
                                        <Input type="date" className={DENSE_INPUT_CLASS}/>
                                    </Form.Item>
                                )}
                                {documentCategory === 'pos_receipt' && (
                                    <Form.Item name="normalized_time" label="Tijd" required className="col-span-6 md:col-span-3">
                                        <Input type="time" className={DENSE_INPUT_CLASS}/>
                                    </Form.Item>
                                )}
                                <Form.Item name="payment_reference" label="Betalingskenmerk" className="col-span-6 md:col-span-3">
                                    <Input type="text" selectOnFocus placeholder="Optioneel" className={DENSE_INPUT_CLASS}/>
                                </Form.Item>
                            </div>
                        </div>
                    </section>

                    <section
                        ref={lineItemsSectionRef}
                        aria-labelledby="line-items-title"
                        className={cn(
                            'flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm ring-amber-300 transition-shadow duration-300',
                            isLineItemsHighlighted && 'ring-2',
                        )}
                    >
                        <SectionTitle
                            accessory={
                                <TableActions
                                    canBookDocument={canBookDocument}
                                    lineItemIds={lineItemIds}

                                    handleAddRow={handleAddRow}
                                    setIsBulkActionsOpen={setIsBulkActionsOpen}
                                    isNetAmountLocked={isNetAmountLocked}
                                    onNetAmountLockedChange={(locked) => {
                                        setIsNetAmountLocked(locked);
                                        setNetAmountLockStatus(locked);
                                    }}
                                />
                            }
                        >
                            <span id="line-items-title">Boekingsregels</span>
                            <span className="ml-2 rounded-full border border-gray-200 bg-gray-50 px-2 py-px text-[10px] font-semibold text-gray-500">
                                {lineItemIds.length === 1 ? '1 regel' : `${lineItemIds.length} regels`}
                            </span>
                        </SectionTitle>
                        {isScannedTotalsBannerShown && (
                            <ScannedTotalsBanner onReview={() => setIsScannedTotalsOpen(true)} />
                        )}
                        <LineItemsTable
                            lineItemIds={lineItemIds}
                            billedLineAmounts={billedLineAmounts}
                            monetaryOrigin={selectedCurrency === 'EUR' ? monetaryOrigin : null}
                            lineItemsNeedingAttention={lineItemsNeedingAttention}
                            netAmountsSum={lineItemTotals.netAmountsSum}
                            vatAmountsSum={lineItemTotals.vatAmountsSum}
                            form={form}
                            glAccounts={glAccounts}
                            documentDirection={documentDirection}
                            defaultVatRateOptions={defaultVatRateOptions}
                            bookingProvider={bookingProvider}
                            currency={currency}
                            invalid={lineItemsError !== null}
                            disabled={isBooked}
                            onAddRow={handleAddRow}
                            onVatRateChange={handleLineItemVatRateChange}
                            onNetAmountChange={handleLineItemNetAmountChange}
                            onDeleteRow={handleDeleteRow}
                            shouldShowBookingError={shouldShowBookingError}
                        />
                        {lineItemsError && (
                            <p role="alert" className={fieldErrorClasses}>{lineItemsError}</p>
                        )}
                    </section>

                    <section
                        aria-labelledby="totals-title"
                        className={cn(
                            'flex flex-col gap-3 border border-gray-200 bg-white py-3 transition-shadow duration-150',
                            canStickTotals && 'sticky -bottom-3 z-10',
                            canStickTotals && isTotalsStuck
                                ? '-mx-4 border-x-transparent border-b-transparent px-7 shadow-[0_-4px_12px_rgb(0_0_0/0.08)]'
                                : 'rounded-xl px-3 shadow-sm',
                        )}
                    >
                        <SectionTitle><span id="totals-title">Totalen</span></SectionTitle>
                        <div className="flex flex-wrap items-start gap-x-3 gap-y-3 [container-type:inline-size]">
                            <Form.Item name="currency" label="Valuta" className={cn('w-28 shrink-0', DENSE_DROPDOWN_CLASS)}>
                                <FormDropdown options={CURRENCY_OPTIONS.map(c => ({value: c.value, label: c.value}))}/>
                            </Form.Item>
                            <Form.Item name="total_amount_payable" label="Totaalbedrag" required className="w-40 shrink-0">
                                <MoneyInput
                                    locale="nl-NL"
                                    currency={currency}
                                    maximumFractionDigits={2}
                                    selectOnFocus
                                    className={cn(DENSE_INPUT_CLASS, 'text-right tabular-nums font-semibold')}
                                />
                            </Form.Item>
                            {monetaryOrigin !== null && selectedCurrency === 'EUR' && (
                                <div className="shrink-0 pt-8">
                                    <ConvertedAmount origin={monetaryOrigin}/>
                                </div>
                            )}
                            {selectedCurrency !== 'EUR' && document.can_convert_to_euro && onConvertToEuro && (
                                <button
                                    type="button"
                                    disabled={isConverting || isSaving || saveBlocker !== null}
                                    aria-busy={isConverting}
                                    title={
                                        saveBlocker === 'invalid' && !isConverting
                                            ? 'Los eerst de openstaande punten op'
                                            : undefined
                                    }
                                    onClick={async () => {
                                        setIsConverting(true);
                                        try {
                                            // Convert reads the stored bill, so the currency and
                                            // amounts on screen have to be saved before it runs or
                                            // it converts from whatever was there before.
                                            await handleSave(
                                                buildPayload(
                                                    form.getFieldsValue(true) as EditorFormValues,
                                                    lineItemIds,
                                                ),
                                                'save',
                                                { handOff: false },
                                            );
                                            await onConvertToEuro();
                                        } finally {
                                            setIsConverting(false);
                                        }
                                    }}
                                    className="mt-8 inline-flex shrink-0 items-center gap-1 rounded text-xs text-primary hover:text-darkprimary disabled:text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                >
                                    <i
                                        className={`${isConverting ? 'ri-loader-4-line animate-spin' : 'ri-exchange-line'} text-sm leading-none`}
                                        aria-hidden="true"
                                    />
                                    {isConverting ? 'Omrekenen…' : 'Omrekenen naar euro'}
                                </button>
                            )}
                            <Form.Item name="vat_amount_overall" label="Totaal BTW" required className="w-40 shrink-0">
                                <MoneyInput
                                    locale="nl-NL"
                                    currency={currency}
                                    maximumFractionDigits={2}
                                    selectOnFocus
                                    className={cn(DENSE_INPUT_CLASS, 'text-right tabular-nums font-semibold')}
                                />
                            </Form.Item>
                            {totalsDifference && (
                                <div className="-mt-1.5 basis-full [@container_(min-width:52rem)]:mt-0 [@container_(min-width:52rem)]:flex-1 [@container_(min-width:52rem)]:basis-0 [@container_(min-width:52rem)]:pt-8">
                                    <TotalsCheck
                                        grossDiffCents={totalsDifference.grossDiff}
                                        vatDiffCents={totalsDifference.vatDiff}
                                        isWithinTolerance={totalsDifference.isWithinTolerance}
                                        currency={currency}
                                        onAdjustTotals={handleAdjustTotals}
                                    />
                                </div>
                            )}
                        </div>
                    </section>
                    <div ref={totalsSentinelRef} aria-hidden="true" className="!mt-0 h-px" />
                </Form>
                </div>

                <EditorActions
                    saveState={saveState}
                    saveBlocker={saveBlocker}
                    isDirty={isDirty}
                    onSave={submit}
                    saveMode={saveMode}
                    saveNudge={saveNudge}
                />
            </div>

            <BulkActionsPopup
                open={isBulkActionsOpen}
                onClose={() => setIsBulkActionsOpen(false)}
                onApply={handleBulkApply}
                glAccounts={glAccounts}
                documentDirection={documentDirection}
                defaultVatRateOptions={defaultVatRateOptions}
                bookingProvider={bookingProvider}
            />

            <ApplyScannedTotalsPopup
                open={isScannedTotalsOpen}
                onClose={() => setIsScannedTotalsOpen(false)}
                onApply={handleApplyScannedTotals}
                lines={scannedTotalLines}
                currency={currency}
            />

        </>
    );
}
